import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, LayoutGrid, Clock, CheckCircle2, CalendarCheck, Trash2, Car, Phone, Mail, Loader2,
  Bell, ImagePlus, Plus, X, Images, CalendarDays, Users, Settings as SettingsIcon, Volume2, VolumeX, BellRing, Wrench, Star, MessageSquare, Siren,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { api, formatApiError } from "@/lib/api";
import { AppointmentChat } from "@/components/AppointmentChat";
import { playNotificationSound } from "@/lib/notify";
import { enablePush, pushSupported } from "@/lib/push";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/context/LanguageContext";
import { AgendaView } from "@/components/admin/AgendaView";
import { ClientsView } from "@/components/admin/ClientsView";
import { SettingsView } from "@/components/admin/SettingsView";
import { ServicesView } from "@/components/admin/ServicesView";
import { ReviewsView } from "@/components/admin/ReviewsView";
import { DepannagesView } from "@/components/admin/DepannagesView";
import { QuoteModal } from "@/components/admin/QuoteModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const STATUS_CLS = {
  pending: "text-amber-400 border-amber-400/40 bg-amber-400/10",
  quoted: "text-fuchsia-400 border-fuchsia-400/40 bg-fuchsia-400/10",
  confirmed: "text-[#00E5FF] border-[#00E5FF]/40 bg-[#00E5FF]/10",
  completed: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10",
  cancelled: "text-zinc-400 border-zinc-500/40 bg-zinc-500/10",
  declined: "text-[#FF1A1A] border-[#FF1A1A]/40 bg-[#FF1A1A]/10",
};
const ALL_STATUS = ["pending", "quoted", "confirmed", "completed", "cancelled", "declined"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { t, tf } = useLang();
  const navigate = useNavigate();
  const [appts, setAppts] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [unread, setUnread] = useState(0);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("gts_sound") !== "0");
  const [pushOn, setPushOn] = useState(false);
  const prevUnread = useRef(0);
  const [gallery, setGallery] = useState([]);
  const [gForm, setGForm] = useState({ title: "", description: "", before_url: "", after_url: "" });
  const [uploading, setUploading] = useState("");
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const [chatOpen, setChatOpen] = useState(null);
  const [chatMsgs, setChatMsgs] = useState([]);

  const openChat = async (id) => {
    if (chatOpen === id) { setChatOpen(null); return; }
    setChatOpen(id);
    setChatMsgs([]);
    try {
      const { data } = await api.get(`/appointments/${id}/messages`);
      setChatMsgs(data.messages || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendChat = async (id, text) => {
    try {
      const { data } = await api.post(`/appointments/${id}/messages`, { text });
      setChatMsgs((prev) => [...prev, data]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = async () => {
    try {
      const [a, s] = await Promise.all([api.get("/appointments"), api.get("/stats")]);
      setAppts(a.data);
      setStats(s.data);
      setUnread(s.data.unread || 0);
      prevUnread.current = s.data.unread || 0;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const loadGallery = async () => {
    try { const { data } = await api.get("/gallery"); setGallery(data); } catch (e) { /* noop */ }
  };

  useEffect(() => { load(); loadGallery(); }, []);

  // Poll for new requests; play a sound + toast when a new one arrives.
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/stats");
        const u = data.unread || 0;
        if (u > prevUnread.current) {
          if (localStorage.getItem("gts_sound") !== "0") playNotificationSound();
          toast(t("admin.new_request"));
          load();
        } else {
          prevUnread.current = u;
          setUnread(u);
          setStats(data);
        }
      } catch (e) { /* noop */ }
    }, 15000);
    return () => clearInterval(id);
  }, []);

  const toggleSound = () => {
    setSoundOn((p) => {
      const next = !p;
      localStorage.setItem("gts_sound", next ? "1" : "0");
      if (next) playNotificationSound();
      return next;
    });
  };

  useEffect(() => {
    if (pushSupported() && Notification.permission === "granted") setPushOn(true);
  }, []);

  const doEnablePush = async () => {
    try {
      await enablePush();
      setPushOn(true);
      toast.success(t("admin.push_enabled"));
    } catch (e) {
      if (e.message === "unsupported") toast.error(t("admin.push_unsupported"));
      else if (e.message === "denied") toast.error(t("admin.push_denied"));
      else toast.error(t("admin.push_error"));
    }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/appointments/${id}/status`, { status }); toast.success(t("admin.toast_status")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/appointments/${id}`); toast.success(t("admin.toast_deleted")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const markAllRead = async () => {
    try { await api.post("/notifications/mark-read"); setUnread(0); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const uploadImage = async (file, field) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error(t("admin.toast_img_format")); return; }
    setUploading(field);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGForm((f) => ({ ...f, [field]: data.url }));
      toast.success(t("admin.toast_uploaded"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(""); }
  };
  const addGalleryItem = async () => {
    if (!gForm.title || !gForm.before_url || !gForm.after_url) { toast.error(t("admin.toast_g_required")); return; }
    try {
      await api.post("/gallery", gForm);
      toast.success(t("admin.toast_g_added"));
      setGForm({ title: "", description: "", before_url: "", after_url: "" });
      if (beforeRef.current) beforeRef.current.value = "";
      if (afterRef.current) afterRef.current.value = "";
      loadGallery();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteGalleryItem = async (id) => {
    try { await api.delete(`/gallery/${id}`); toast.success(t("admin.toast_g_deleted")); loadGallery(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doLogout = () => { logout(); navigate("/admin"); };
  const filtered = filter === "all" ? appts : appts.filter((a) => a.status === filter);

  const cards = [
    { key: "total", label: t("admin.stat_total"), value: stats.total, icon: LayoutGrid, color: "text-white" },
    { key: "pending", label: t("admin.stat_pending"), value: stats.pending, icon: Clock, color: "text-amber-400" },
    { key: "confirmed", label: t("admin.stat_confirmed"), value: stats.confirmed, icon: CalendarCheck, color: "text-[#00E5FF]" },
    { key: "completed", label: t("admin.stat_completed"), value: stats.completed, icon: CheckCircle2, color: "text-emerald-400" },
  ];

  const tabBtn = "rounded-none data-[state=active]:bg-[#E60000] data-[state=active]:text-white font-mono-tech text-xs uppercase tracking-[0.15em] px-5 py-2.5";

  return (
    <div className="noise-overlay bg-[#050505] min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-black/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <ThemeToggle className="w-10 h-10" />
            <button onClick={toggleSound} data-testid="admin-sound-toggle" title={soundOn ? t("admin.sound_on") : t("admin.sound_off")} className="w-10 h-10 flex items-center justify-center border border-white/15 text-zinc-300 hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors">
              {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button onClick={doEnablePush} data-testid="admin-push-toggle" title={pushOn ? t("admin.push_on") : t("admin.push_enable")} className={`w-10 h-10 flex items-center justify-center border transition-colors ${pushOn ? "border-[#00E5FF] text-[#00E5FF]" : "border-white/15 text-zinc-300 hover:border-[#00E5FF] hover:text-[#00E5FF]"}`}>
              <BellRing className="w-4 h-4" />
            </button>
            <button onClick={markAllRead} data-testid="admin-notifications" title={t("admin.notif_title")} className="relative w-10 h-10 flex items-center justify-center border border-white/15 text-zinc-300 hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors">
              <Bell className="w-4 h-4" />
              {unread > 0 && <span data-testid="notif-badge" className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 flex items-center justify-center bg-[#E60000] text-white text-[10px] font-mono-tech font-700 rounded-full ia-badge-glow">{unread}</span>}
            </button>
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 hidden lg:block">{user?.email}</span>
            <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white text-sm uppercase tracking-wide font-mono-tech hover:border-[#E60000] hover:text-[#E60000] transition-colors">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">{t("admin.logout")}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display font-900 text-white text-3xl tracking-tighter uppercase mb-8">{t("admin.dashboard")}</h1>

        <Tabs defaultValue="agenda" className="w-full">
          <TabsList className="bg-[#0C0C0E] border border-white/10 rounded-none p-1 mb-8 h-auto flex-wrap">
            <TabsTrigger value="agenda" data-testid="tab-agenda" className={tabBtn}><CalendarDays className="w-4 h-4 mr-2" /> {t("admin.tab_agenda")}</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="tab-appointments" className={tabBtn}><CalendarCheck className="w-4 h-4 mr-2" /> {t("admin.tab_appts")}</TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients" className={tabBtn}><Users className="w-4 h-4 mr-2" /> {t("admin.tab_clients")}</TabsTrigger>
            <TabsTrigger value="depannage" data-testid="tab-depannage" className={tabBtn}><Siren className="w-4 h-4 mr-2" /> {t("admin.tab_depannage")}</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery" className={tabBtn}><Images className="w-4 h-4 mr-2" /> {t("admin.tab_gallery")}</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services" className={tabBtn}><Wrench className="w-4 h-4 mr-2" /> {t("admin.tab_services")}</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews" className={tabBtn}><Star className="w-4 h-4 mr-2" /> {t("admin.tab_reviews")}</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className={tabBtn}><SettingsIcon className="w-4 h-4 mr-2" /> {t("admin.tab_settings")}</TabsTrigger>
          </TabsList>

          <TabsContent value="agenda" className="mt-0"><AgendaView appts={appts} /></TabsContent>
          <TabsContent value="clients" className="mt-0"><ClientsView /></TabsContent>
          <TabsContent value="depannage" className="mt-0"><DepannagesView /></TabsContent>
          <TabsContent value="services" className="mt-0"><ServicesView /></TabsContent>
          <TabsContent value="reviews" className="mt-0"><ReviewsView /></TabsContent>
          <TabsContent value="settings" className="mt-0"><SettingsView /></TabsContent>

          <TabsContent value="appointments" className="mt-0">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mb-10">
              {cards.map((c) => (
                <div key={c.key} className="bg-[#0C0C0E] p-6" data-testid={`stat-${c.key}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500">{c.label}</span>
                    <c.icon className={`w-4 h-4 ${c.color}`} />
                  </div>
                  <span className={`font-display font-900 text-4xl tracking-tighter ${c.color}`}>{c.value}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-6">
              {["all", ...ALL_STATUS].map((f) => (
                <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`} className={`px-4 py-2 font-mono-tech text-[11px] uppercase tracking-[0.15em] border transition-colors ${filter === f ? "border-[#E60000] text-[#E60000] bg-[#E60000]/10" : "border-white/10 text-zinc-500 hover:text-white"}`}>
                  {f === "all" ? t("admin.f_all") : t(`status.${f}`)}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-24 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="border border-white/10 bg-[#0C0C0E] py-20 text-center text-zinc-500 font-mono-tech text-sm" data-testid="admin-empty">{t("admin.empty")}</div>
            ) : (
              <div className="space-y-px bg-white/10 border border-white/10">
                {filtered.map((a) => (
                  <div key={a.id} className="bg-[#0C0C0E] p-5 sm:p-6" data-testid={`appt-row-${a.id}`}>
                    <div className="grid lg:grid-cols-12 gap-5 items-start">
                    <div className="lg:col-span-4">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-display font-700 text-white text-lg">{a.name}</span>
                        <span className={`px-2 py-0.5 text-[10px] font-mono-tech uppercase tracking-wider border ${STATUS_CLS[a.status]}`}>{t(`status.${a.status}`)}</span>
                        {!a.read && <span className="px-2 py-0.5 text-[10px] font-mono-tech uppercase tracking-wider border border-[#E60000] text-[#E60000] bg-[#E60000]/10 ia-badge-glow">{t("admin.new_tag")}</span>}
                      </div>
                      <div className="mt-2 space-y-1.5 text-sm">
                        <a href={`mailto:${a.email}`} data-testid={`appt-email-${a.id}`} className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors w-fit"><Mail className="w-3.5 h-3.5" /> {a.email}</a>
                        {a.phone && (
                          <a href={`tel:${a.phone.replace(/\s/g, "")}`} data-testid={`appt-phone-${a.id}`} title={t("admin.call_client")} className="inline-flex items-center gap-2 px-2.5 py-1 border border-[#00E5FF]/40 bg-[#00E5FF]/10 text-[#00E5FF] font-mono-tech font-700 hover:bg-[#00E5FF] hover:text-black transition-colors w-fit">
                            <Phone className="w-3.5 h-3.5" /> {a.phone}
                          </a>
                        )}
                      </div>
                    </div>

                    <div className="lg:col-span-4 font-mono-tech text-sm">
                      <div className="text-[#00E5FF]">{tf(`services.${a.service_id}.label`, a.service_label)}</div>
                      <div className="text-zinc-400 mt-1">{a.preferred_date} · {a.preferred_time}</div>
                      {a.vehicle && Object.values(a.vehicle).some((x) => x != null && String(x).trim() !== "") && (
                        <div className="mt-3 border border-[#00E5FF]/25 bg-[#00E5FF]/5 p-3" data-testid={`appt-vehicle-${a.id}`}>
                          <div className="flex items-center gap-2 text-[#00E5FF] font-mono-tech text-[10px] uppercase tracking-[0.15em] mb-2">
                            <Car className="w-3.5 h-3.5" /> {t("admin.veh_details")}
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            {[
                              ["marque", t("admin.veh_marque")],
                              ["modele", t("admin.veh_modele")],
                              ["immatriculation", t("admin.veh_immat")],
                              ["vin", t("admin.veh_vin")],
                              ["annee", t("admin.veh_annee")],
                              ["cylindree", t("admin.veh_cylindree")],
                              ["energie", t("admin.veh_energie")],
                              ["puissance", t("admin.veh_puissance")],
                              ["couleur", t("admin.veh_couleur")],
                              ["date_immatriculation", t("admin.veh_first_reg")],
                              ["proprietaire", t("admin.veh_owner")],
                            ].filter(([k]) => a.vehicle[k] != null && String(a.vehicle[k]).trim() !== "").map(([k, label]) => (
                              <div key={k} className="flex justify-between gap-2 py-0.5 border-b border-white/5">
                                <span className="text-zinc-600">{label}</span>
                                <span className="text-white text-right break-all">{a.vehicle[k]}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {a.quote?.total != null && <div className="text-fuchsia-300 mt-1.5">{t("admin.quote_total")} : {Number(a.quote.total).toFixed(2)} €</div>}
                      {a.message && <p className="text-zinc-600 text-xs mt-2 italic max-w-md">“{a.message}”</p>}
                    </div>

                    <div className="lg:col-span-4 flex items-center gap-2 justify-start lg:justify-end flex-wrap">
                      <QuoteModal appt={a} onSaved={load} />
                      <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                        <SelectTrigger data-testid={`status-select-${a.id}`} className="w-[150px] bg-[#050505] border-white/10 rounded-none text-white text-sm focus:ring-0"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0C0C0E] border-white/10 text-white rounded-none">
                          {ALL_STATUS.map((k) => (
                            <SelectItem key={k} value={k} data-testid={`status-option-${k}`} className="focus:bg-white/10 rounded-none">{t(`status.${k}`)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <button onClick={() => remove(a.id)} data-testid={`delete-${a.id}`} className="w-10 h-10 flex items-center justify-center border border-white/10 text-zinc-500 hover:border-[#E60000] hover:text-[#E60000] transition-colors"><Trash2 className="w-4 h-4" /></button>
                      <button onClick={() => openChat(a.id)} data-testid={`chat-toggle-${a.id}`} title={t("admin.chat_toggle")} className={`relative w-10 h-10 flex items-center justify-center border transition-colors ${chatOpen === a.id ? "border-[#00E5FF] text-[#00E5FF]" : "border-white/10 text-zinc-500 hover:border-[#00E5FF] hover:text-[#00E5FF]"}`}>
                        <MessageSquare className="w-4 h-4" />
                        {(a.chat_messages?.length || 0) > 0 && <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-[#00E5FF] text-black text-[9px] font-mono-tech font-700 rounded-full">{a.chat_messages.length}</span>}
                      </button>
                    </div>
                    </div>
                    {chatOpen === a.id && (
                      <div className="mt-5 pt-5 border-t border-white/10">
                        <div className="flex items-center gap-2 mb-4">
                          <MessageSquare className="w-4 h-4 text-[#00E5FF]" />
                          <span className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-[#00E5FF]">{t("admin.chat_title")}</span>
                        </div>
                        <AppointmentChat messages={chatMsgs} onSend={(text) => sendChat(a.id, text)} viewerRole="admin" testidPrefix={`admin-chat-${a.id}`} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="gallery" className="mt-0">
            <div className="border border-white/10 bg-[#0C0C0E] p-6 mb-8">
              <h3 className="font-display font-700 text-white text-lg tracking-tight mb-5 flex items-center gap-2"><Plus className="w-4 h-4 text-[#E60000]" /> {t("admin.add_real")}</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">{t("admin.g_title")}</label>
                  <Input value={gForm.title} onChange={(e) => setGForm((f) => ({ ...f, title: e.target.value }))} data-testid="gallery-title" placeholder={t("admin.g_title_ph")} className="bg-[#050505] border-white/10 rounded-none h-12 text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0" />
                </div>
                <div className="sm:col-span-2">
                  <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">{t("admin.g_desc")}</label>
                  <Textarea value={gForm.description} onChange={(e) => setGForm((f) => ({ ...f, description: e.target.value }))} data-testid="gallery-description" placeholder={t("admin.g_desc_ph")} className="bg-[#050505] border-white/10 rounded-none text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0 min-h-[80px]" />
                </div>
                {[
                  { field: "before_url", label: t("admin.g_before"), ref: beforeRef },
                  { field: "after_url", label: t("admin.g_after"), ref: afterRef },
                ].map(({ field, label, ref }) => (
                  <div key={field}>
                    <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">{label}</label>
                    <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`gallery-file-${field}`} onChange={(e) => uploadImage(e.target.files?.[0], field)} />
                    {gForm[field] ? (
                      <div className="relative border border-white/10 h-40 overflow-hidden group">
                        <img src={resolveUrl(gForm[field])} alt={label} className="w-full h-full object-cover" />
                        <button onClick={() => setGForm((f) => ({ ...f, [field]: "" }))} className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-black/70 text-white hover:bg-[#E60000] transition-colors" data-testid={`gallery-clear-${field}`}><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : (
                      <button onClick={() => ref.current?.click()} disabled={uploading === field} data-testid={`gallery-upload-${field}`} className="w-full h-40 border border-dashed border-white/20 hover:border-[#00E5FF] bg-[#050505] flex flex-col items-center justify-center gap-2 text-zinc-500 hover:text-[#00E5FF] transition-colors">
                        {uploading === field ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-6 h-6" />}
                        <span className="font-mono-tech text-[10px] uppercase tracking-[0.15em]">{uploading === field ? t("admin.uploading") : t("admin.choose_img")}</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={addGalleryItem} data-testid="gallery-add-submit" className="mt-6 inline-flex items-center gap-2 px-7 py-3 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors"><Plus className="w-4 h-4" /> {t("admin.publish")}</button>
            </div>

            {gallery.length === 0 ? (
              <div className="border border-white/10 bg-[#0C0C0E] py-16 text-center text-zinc-500 font-mono-tech text-sm" data-testid="gallery-empty">{t("admin.gallery_empty")}</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                {gallery.map((g) => (
                  <div key={g.id} className="bg-[#0C0C0E] p-4" data-testid={`gallery-item-${g.id}`}>
                    <div className="grid grid-cols-2 gap-1 mb-3">
                      <div className="relative aspect-square overflow-hidden">
                        <img src={resolveUrl(g.before_url)} alt="before" className="w-full h-full object-cover" />
                        <span className="absolute top-1.5 left-1.5 font-mono-tech text-[8px] uppercase tracking-wider text-[#FF1A1A] bg-black/70 px-1.5 py-0.5">{t("admin.before")}</span>
                      </div>
                      <div className="relative aspect-square overflow-hidden">
                        <img src={resolveUrl(g.after_url)} alt="after" className="w-full h-full object-cover" />
                        <span className="absolute top-1.5 left-1.5 font-mono-tech text-[8px] uppercase tracking-wider text-[#00E5FF] bg-black/70 px-1.5 py-0.5">{t("admin.after")}</span>
                      </div>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="font-display font-700 text-white text-sm">{g.title}</h4>
                        {g.description && <p className="text-zinc-600 text-xs mt-1 leading-relaxed">{g.description}</p>}
                      </div>
                      <button onClick={() => deleteGalleryItem(g.id)} data-testid={`gallery-delete-${g.id}`} className="shrink-0 w-8 h-8 flex items-center justify-center border border-white/10 text-zinc-500 hover:border-[#E60000] hover:text-[#E60000] transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
